import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { GripVertical, Trash2, Star, Clock, RotateCcw } from "lucide-react";
import ExerciseBody from "../components/ExerciseBody";

export default function Admin() {
  const { user } = useAuth();
  const [lessons, setLessons] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState({ title: "", body: "" });
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [grantEmails, setGrantEmails] = useState("");
  const [granting, setGranting] = useState(false);
  const [grantResult, setGrantResult] = useState(null);
  const [signups, setSignups] = useState(null);
  const [signupsLoading, setSignupsLoading] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const bodyRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/lessons");
    setLessons(data);
    if (!selectedId && data[0]) selectAt(data[0].id, data);
  };

  const selectAt = async (id, listArg) => {
    setSelectedId(id);
    setShowHistory(false);
    const { data } = await api.get(`/lessons/${id}`);
    setDraft({ title: data.title, body: data.body });
  };

  const loadHistory = async (id) => {
    try {
      const { data } = await api.get(`/admin/lessons/${id}/history`);
      setHistory(data.versions || []);
      setShowHistory(true);
    } catch (e) {
      toast.error("could not load history");
    }
  };

  const restoreVersion = async (versionId) => {
    if (!window.confirm("restore this version? current body will be saved to history first.")) return;
    try {
      const { data } = await api.post(`/admin/lessons/${selectedId}/restore/${versionId}`);
      setDraft({ title: data.title, body: data.body });
      toast.success("restored");
      setShowHistory(false);
      await load();
    } catch (e) { toast.error("restore failed"); }
  };

  const insertAtCursor = (snippet) => {
    const el = bodyRef.current;
    if (!el) return;
    const start = el.selectionStart ?? draft.body.length;
    const end = el.selectionEnd ?? draft.body.length;
    const nextBody = draft.body.slice(0, start) + snippet + draft.body.slice(end);
    setDraft((d) => ({ ...d, body: nextBody }));
    setTimeout(() => {
      el.focus();
      const pos = start + snippet.length;
      el.setSelectionRange(pos, pos);
    }, 0);
  };

  const insertWriting = () => {
    const key = window.prompt("key (unique identifier, letters/dashes only):", "the-efficiency-audit");
    if (!key) return;
    const label = window.prompt("label (shown to the reader):", "the efficiency audit");
    if (label === null) return;
    insertAtCursor(`\n[[writing key="${key.trim()}" label="${label.trim()}"]]\n`);
  };

  const insertLedger = () => {
    const key = window.prompt("key (unique identifier, letters/dashes only):", "cost-benefit-ledger");
    if (!key) return;
    const left = window.prompt("left column label:", "what respectability gave you");
    if (left === null) return;
    const right = window.prompt("right column label:", "what it cost you");
    if (right === null) return;
    insertAtCursor(`\n[[ledger key="${key.trim()}" left="${left.trim()}" right="${right.trim()}"]]\n`);
  };

  const insertLog = () => {
    const key = window.prompt("key (unique identifier, letters/dashes only):", "evidence-journal");
    if (!key) return;
    const label = window.prompt("label (shown as a heading):", "evidence journal");
    if (label === null) return;
    const entryLabel = window.prompt("entry input label (e.g. 'tonight's evidence'):", "tonight's evidence");
    if (entryLabel === null) return;
    const datedChoice = window.confirm("stamp each entry with a date? (ok = yes, cancel = no)");
    insertAtCursor(`\n[[log key="${key.trim()}" label="${label.trim()}" entrylabel="${entryLabel.trim()}" dated="${datedChoice ? "true" : "false"}"]]\n`);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (user?.is_admin) loadSignups(); /* eslint-disable-next-line */ }, [user?.is_admin]);

  if (user === null) return null;
  if (user && !user.is_admin) return <Navigate to="/course" replace />;

  const save = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      await api.patch(`/admin/lessons/${selectedId}`, { title: draft.title, body: draft.body });
      toast.success("saved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "save failed");
    } finally { setSaving(false); }
  };

  const toggleFree = async (id) => {
    try {
      await api.patch(`/admin/lessons/${id}`, { is_free: true });
      toast.success("free preview updated");
      await load();
    } catch (e) { toast.error("update failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("delete this lesson?")) return;
    await api.delete(`/admin/lessons/${id}`);
    if (id === selectedId) setSelectedId(null);
    await load();
  };

  const create = async () => {
    const nextOrder = lessons.length;
    const slug = `lesson-${Date.now()}`;
    const { data } = await api.post("/admin/lessons", {
      title: "new lesson", slug, body: "<p></p>", order: nextOrder, is_free: false,
    });
    await load();
    selectAt(data.id);
  };

  const move = async (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= lessons.length) return;
    const copy = [...lessons];
    [copy[idx], copy[newIdx]] = [copy[newIdx], copy[idx]];
    setLessons(copy);
    await api.post("/admin/lessons/reorder", { ordered_ids: copy.map((l) => l.id) });
    await load();
  };

  const grantAccess = async () => {
    const emails = grantEmails
      .split(/[\s,;\n]+/)
      .map((e) => e.trim())
      .filter(Boolean);
    if (emails.length === 0) {
      toast.error("paste at least one email");
      return;
    }
    setGranting(true);
    setGrantResult(null);
    try {
      const { data } = await api.post("/admin/grant-access", { emails });
      setGrantResult(data);
      setGrantEmails("");
      toast.success(data.summary);
      // refresh recent signups so newly granted rows show up
      loadSignups();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not grant access");
    } finally {
      setGranting(false);
    }
  };

  const loadSignups = async () => {
    setSignupsLoading(true);
    try {
      const { data } = await api.get("/admin/signups");
      setSignups(data);
    } catch (e) {
      toast.error("could not load signups");
    } finally {
      setSignupsLoading(false);
    }
  };

  const backfillKit = async () => {
    if (!window.confirm("push all unsynced free-chapter signups to kit? this is safe to run multiple times.")) return;
    setBackfilling(true);
    try {
      const { data } = await api.post("/admin/kit-backfill");
      toast.success(data.summary);
      loadSignups();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  return (
    <div className="min-h-screen max-w-6xl mx-auto px-6 py-10 grid md:grid-cols-[280px_1fr] gap-8" data-testid="admin-page">
      <aside style={{ borderRight: "1px solid var(--gold-soft)" }} className="md:pr-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-2xl" style={{ color: "var(--ink)" }}>lessons</h2>
          <button onClick={create} className="btn-ghost" data-testid="admin-new">+ new</button>
        </div>
        <ul className="space-y-1" data-testid="admin-list">
          {lessons.map((l, i) => (
            <li key={l.id} className="flex items-center gap-1 group">
              <button
                onClick={() => move(i, -1)}
                className="text-xs opacity-60 hover:opacity-100"
                title="move up"
                data-testid={`admin-up-${i}`}
              ><GripVertical size={12} strokeWidth={1.5} /></button>
              <button
                onClick={() => selectAt(l.id)}
                className={`flex-1 text-left text-sm px-2 py-1.5 transition-colors ${l.id === selectedId ? "font-medium" : ""}`}
                style={{
                  background: l.id === selectedId ? "var(--cream-2)" : "transparent",
                  color: l.id === selectedId ? "var(--ink)" : "var(--ink-soft)",
                }}
                data-testid={`admin-lesson-${i}`}
              >
                <span className="font-serif text-xs mr-2" style={{ color: "var(--gold)" }}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                {l.title}
              </button>
              <button
                onClick={() => toggleFree(l.id)}
                title="make free preview"
                data-testid={`admin-star-${i}`}
                style={{ color: l.is_free ? "var(--gold)" : "var(--ink-faint)" }}
              >
                <Star size={14} strokeWidth={1.5} fill={l.is_free ? "var(--gold)" : "transparent"} />
              </button>
              <button onClick={() => remove(l.id)} data-testid={`admin-delete-${i}`}
                className="opacity-40 hover:opacity-100" title="delete">
                <Trash2 size={12} strokeWidth={1.5} />
              </button>
            </li>
          ))}
        </ul>

        <div className="mt-10 pt-6 border-t" style={{ borderColor: "var(--gold-soft)" }} data-testid="admin-grant-panel">
          <h3 className="font-serif text-lg mb-2" style={{ color: "var(--ink)" }}>grant access</h3>
          <p className="text-xs mb-3" style={{ color: "var(--ink-soft)" }}>
            paste emails (comma, space, or newline separated). each gets lifetime access and an email with a sign-in link.
          </p>
          <textarea
            value={grantEmails}
            onChange={(e) => setGrantEmails(e.target.value)}
            placeholder="jane@example.com, jo@example.com"
            rows={4}
            className="field"
            style={{
              border: "1px solid var(--gold-soft)",
              borderRadius: "2px",
              padding: "0.6rem 0.75rem",
              fontSize: "0.85rem",
              resize: "vertical",
            }}
            data-testid="admin-grant-textarea"
          />
          <button
            onClick={grantAccess}
            disabled={granting}
            className="btn-primary mt-3 w-full"
            data-testid="admin-grant-btn"
          >
            {granting ? "granting…" : "grant access"}
          </button>
          {grantResult && (
            <div className="mt-4 text-xs space-y-1" data-testid="admin-grant-result">
              {grantResult.granted?.length > 0 && (
                <p style={{ color: "var(--ink)" }}>
                  <span style={{ color: "var(--gold)" }}>granted</span> · {grantResult.granted.join(", ")}
                </p>
              )}
              {grantResult.created?.length > 0 && (
                <p style={{ color: "var(--ink)" }}>
                  <span style={{ color: "var(--gold)" }}>created</span> · {grantResult.created.join(", ")}
                </p>
              )}
              {grantResult.already_had_access?.length > 0 && (
                <p style={{ color: "var(--ink-soft)" }}>
                  already had access · {grantResult.already_had_access.join(", ")}
                </p>
              )}
              {grantResult.email_failed?.length > 0 && (
                <p style={{ color: "var(--ink)" }}>
                  <span style={{ color: "var(--gold)" }}>email failed</span> · {grantResult.email_failed.join(", ")}
                </p>
              )}
              {grantResult.invalid?.length > 0 && (
                <p style={{ color: "var(--ink-soft)" }}>
                  invalid · {grantResult.invalid.join(", ")}
                </p>
              )}
              <p className="italic pt-1" style={{ color: "var(--ink-soft)" }}>
                each granted email received a sign-in link. their access is live now.
              </p>
            </div>
          )}
        </div>

        <div className="mt-10 pt-6 border-t" style={{ borderColor: "var(--gold-soft)" }} data-testid="admin-signups-panel">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-serif text-lg" style={{ color: "var(--ink)" }}>recent signups</h3>
            <button
              onClick={loadSignups}
              className="text-xs underline"
              style={{ color: "var(--ink-soft)" }}
              data-testid="admin-signups-refresh"
            >
              refresh
            </button>
          </div>
          {signups && (
            <p className="text-xs mb-3" style={{ color: "var(--ink-soft)" }}>
              {signups.total_free_signups} free-chapter signup{signups.total_free_signups === 1 ? "" : "s"} · {signups.total_granted} granted
            </p>
          )}
          {signups && signups.not_synced_to_kit > 0 && (
            <button
              onClick={backfillKit}
              disabled={backfilling}
              className="btn-ghost w-full mb-3 text-xs"
              data-testid="admin-kit-backfill"
            >
              {backfilling ? "syncing…" : `push ${signups.not_synced_to_kit} unsynced to kit`}
            </button>
          )}
          {signupsLoading && !signups && (
            <p className="text-xs" style={{ color: "var(--ink-soft)" }}>loading…</p>
          )}
          {signups && signups.rows.length === 0 && (
            <p className="text-xs italic" style={{ color: "var(--ink-soft)" }}>no signups yet.</p>
          )}
          {signups && signups.rows.length > 0 && (
            <ul className="space-y-2 max-h-80 overflow-y-auto sidebar-scroll pr-1" data-testid="admin-signups-list">
              {signups.rows.map((row, i) => (
                <li key={row.email + i} className="text-xs" style={{ borderBottom: "1px solid var(--gold-soft)", paddingBottom: "0.4rem" }}>
                  <div className="flex items-center gap-2">
                    <span
                      className="text-[9px] uppercase tracking-widest px-1.5 py-0.5"
                      style={{
                        background: row.kind === "granted" ? "var(--ink)" : "transparent",
                        color: row.kind === "granted" ? "var(--cream)" : "var(--gold)",
                        border: row.kind === "granted" ? "none" : "1px solid var(--gold-soft)",
                        borderRadius: "2px",
                      }}
                    >
                      {row.kind === "granted" ? "granted" : "free"}
                    </span>
                    {row.has_purchased && row.kind !== "granted" && (
                      <span className="text-[9px] uppercase tracking-widest" style={{ color: "var(--gold)" }}>
                        · bought
                      </span>
                    )}
                    {row.kind === "free_signup" && !row.kit_synced && (
                      <span className="text-[9px] uppercase tracking-widest" style={{ color: "var(--ink-faint)" }}>
                        · not in kit
                      </span>
                    )}
                  </div>
                  <div className="mt-1" style={{ color: "var(--ink)" }}>{row.email}</div>
                  {row.first_name && (
                    <div style={{ color: "var(--ink-soft)" }}>{row.first_name}</div>
                  )}
                  <div className="text-[10px]" style={{ color: "var(--ink-faint)" }}>
                    {row.created_at ? row.created_at.slice(0, 16).replace("T", " ") : "—"}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>

      <section>
        {selectedId ? (
          <div className="space-y-6" data-testid="admin-editor">
            <div>
              <label className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>title</label>
              <input
                className="field"
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                data-testid="admin-title"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>
                  body — html + shortcodes
                </label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={insertWriting}
                    className="btn-ghost text-xs"
                    data-testid="admin-insert-writing"
                    title="insert a writing box shortcode"
                  >+ writing box</button>
                  <button
                    type="button"
                    onClick={insertLedger}
                    className="btn-ghost text-xs"
                    data-testid="admin-insert-ledger"
                    title="insert a two-column ledger shortcode"
                  >+ ledger</button>
                  <button
                    type="button"
                    onClick={insertLog}
                    className="btn-ghost text-xs"
                    data-testid="admin-insert-log"
                    title="insert a dated running log shortcode (evidence journal etc)"
                  >+ log</button>
                  <button
                    type="button"
                    onClick={() => loadHistory(selectedId)}
                    className="btn-ghost text-xs inline-flex items-center gap-1"
                    data-testid="admin-history"
                    title="see previous versions of this lesson body"
                  ><Clock size={12} strokeWidth={1.5} /> history</button>
                </div>
              </div>
              <p className="text-xs mb-1" style={{ color: "var(--ink-soft)" }}>
                supports &lt;h2&gt;, &lt;p&gt;, &lt;blockquote&gt; (pull quotes), &lt;em&gt;, &lt;strong&gt;. use <strong>+ writing box</strong> above to insert a saveable text field, or <strong>+ ledger</strong> for the two-column exercise.
              </p>
              <textarea
                ref={bodyRef}
                rows={22}
                style={{ borderBottom: "none", border: "1px solid var(--gold-soft)", padding: "1rem", fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: "0.9rem", background: "var(--cream)", color: "var(--ink)", width: "100%" }}
                value={draft.body}
                onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                data-testid="admin-body"
              />
            </div>

            {showHistory && (
              <div className="border-t pt-6" style={{ borderColor: "var(--gold-soft)" }} data-testid="admin-history-panel">
                <p className="text-xs uppercase tracking-widest mb-3" style={{ color: "var(--gold)" }}>version history</p>
                {history.length === 0 ? (
                  <p className="text-sm" style={{ color: "var(--ink-soft)" }}>no previous versions yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {history.map((v) => (
                      <li key={v.id} className="flex items-center justify-between gap-3 text-sm p-2" style={{ background: "var(--cream-2)" }}>
                        <span style={{ color: "var(--ink-soft)" }}>
                          {new Date(v.changed_at).toLocaleString()} — {v.prev_length} chars → {v.new_length} chars {v.note ? `(${v.note})` : ""}
                        </span>
                        <button
                          onClick={() => restoreVersion(v.id)}
                          className="btn-ghost inline-flex items-center gap-1 text-xs"
                          data-testid={`admin-restore-${v.id}`}
                        >
                          <RotateCcw size={12} strokeWidth={1.5} /> restore
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <div className="flex items-center gap-3">
              <button onClick={save} disabled={saving} className="btn-primary" data-testid="admin-save">
                {saving ? "saving…" : "save"}
              </button>
              <span className="text-sm" style={{ color: "var(--ink-soft)" }}>
                tip: paste chapter text below. wrap paragraphs in &lt;p&gt;…&lt;/p&gt;.
              </span>
            </div>

            <div className="mt-10 pt-6 border-t" style={{ borderColor: "var(--gold-soft)" }}>
              <p className="text-xs uppercase tracking-widest mb-3" style={{ color: "var(--gold)" }}>preview — this is exactly what a reader sees</p>
              <div style={{ background: "var(--cream-2)", padding: "1.5rem", borderRadius: "2px", border: "1px solid var(--gold-soft)" }}>
                <ExerciseBody html={draft.body} lessonId={selectedId} unlocked={true} previewMode={true} />
              </div>
              <p className="text-xs mt-2" style={{ color: "var(--ink-soft)" }}>
                shortcodes like <code>[[writing ...]]</code> and <code>[[ledger ...]]</code> render as saveable input fields when a reader opens the lesson.
              </p>
            </div>
          </div>
        ) : (
          <p style={{ color: "var(--ink-soft)" }}>select a lesson on the left.</p>
        )}
      </section>
    </div>
  );
}
